import { useToast } from '@core/toast'
import { useGetTaskTimeline } from '@core/react-query/features/tasks/task'
import { AuditResult } from '@core/entities/audit/AuditResult'
import { useMemo } from 'react'
import { useGetTaskById } from '@core/react-query/features/tasks/task/hooks/useGetTaskById'
import { TaskActivity } from '@core/entities/task/TaskActivity'
import { Task } from '@core/entities/task/Task'

type UseTaskActivitiesProps = {
  taskId: number
  fetchTask?: boolean
}

export const useTaskActivities = ({
  taskId,
  fetchTask = false,
}: UseTaskActivitiesProps): {
  activityData: Array<TaskActivity>
  isFetching: boolean
  task: Task
} => {
  const toast = useToast()

  const { data: taskTimelineData, isFetching: isFetchingTaskTimeline } =
    useGetTaskTimeline(
      {
        taskId,
      },
      {
        onError: (e) => toast.error(e),
        keepPreviousData: true,
      },
    )

  const { data: task, isFetching: isFetchingTask } = useGetTaskById(
    {
      taskId,
    },
    {
      refetchOnMount: fetchTask,
    },
  )

  const permittedAuditActions = useMemo((): Array<AuditResult> => {
    const permittedAuditActions = [
      'TASK_UPDATED',
      'TASK_CANCELLED',
      'TASK_REOPENED',
      'TASK_REASSIGNED',
      'TASK_CHANGES_REQUESTED',
      'TASK_COMPLETED',
      'TASK_WORK_STEP_SUBMITTED',
      'TASK_APPROVED',
      'TASK_CREATED',
    ]
    return (
      taskTimelineData?.pages
        ?.flatMap((page) => page)
        .filter((audit) => permittedAuditActions.includes(audit.action)) || []
    )
  }, [taskTimelineData])

  const activityData = useMemo((): Array<TaskActivity> => {
    const auditResultActivities =
      permittedAuditActions?.map((audit) =>
        TaskActivity.new({
          type: 'AUDIT_RESULT',
          auditResult: audit,
          date: audit.createdOn,
        }),
      ) || []

    const doneActivities = []
    // when a task is reopened we add a DONE activity before it unless the previous activity is TASK_CANCELLED
    auditResultActivities.forEach((activity) => {
      if (activity.auditResult.action === 'TASK_REOPENED') {
        const previousActivity = auditResultActivities.find(
          (a) => a.date < activity.date,
        )
        if (previousActivity?.auditResult.action !== 'TASK_CANCELLED') {
          doneActivities.push(
            TaskActivity.new({
              type: 'DONE',
              date: previousActivity?.date,
            }),
          )
        }
      }
    })

    // combine in chronological order
    const completedActivities = [
      ...auditResultActivities,
      ...doneActivities,
    ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    const currentStep =
      task?.currentStep &&
      task?.status !== 'CANCELLED' &&
      TaskActivity.new({
        type: 'CURRENT_STEP',
        step: task?.currentStep,
      })

    const remainingSteps =
      task?.template.currentVersion.steps.taskSteps
        ?.filter(
          (step) =>
            ![...(task?.submittedSteps?.steps || []), task?.currentStep].some(
              (s) => s.id === step.id,
            ),
        )
        ?.map((step) =>
          TaskActivity.new({
            type: 'REMAINING_STEP',
            step,
          }),
        ) || []

    const done = TaskActivity.new({
      type: 'DONE',
    })

    return [...completedActivities, currentStep, ...remainingSteps, done]
  }, [task, permittedAuditActions])

  return {
    activityData,
    task,
    isFetching: isFetchingTaskTimeline || isFetchingTask,
  }
}
